import axios from "axios";

/**
 * One axios instance for the whole app.
 *
 * Previously every component built its own request, repeating the base URL and
 * the `X-API-Key` header inline — 22 files, and any change to auth or error
 * handling had to be made in all of them.
 *
 * Note on the key: REACT_APP_* values are compiled into the public bundle, so
 * this header is visible to anyone who opens devtools. It is a rate-limiting
 * token, not authentication. Anything genuinely user-scoped must be authorised
 * server-side on the Auth0 JWT.
 */
const api = axios.create({
  baseURL: process.env.REACT_APP_DATA_API_URL,
  timeout: 30000,
  headers: { "X-API-Key": process.env.REACT_APP_TOKEN_API_URL },
});

/** Pull a human-readable message out of the API's several error shapes. */
export const errorMessage = (error, fallback = "Something went wrong") =>
  error?.response?.data?.detail ||
  error?.response?.data?.error ||
  error?.message ||
  fallback;

// ── Catalogue ──────────────────────────────────────────────────────────────

/** Every filter option for a sport, in one request (replaces four). */
export const getFilters = (params) =>
  api.get("/v1/filters", { params }).then((r) => r.data);

export const getSports = () => api.get("/v1/sports").then((r) => r.data);

export const getSportHub = (sportSlug) =>
  api.get(`/v1/sports/${sportSlug}/teams`).then((r) => r.data);

export const getTeam = (sportSlug, teamSlug, params) =>
  api.get(`/v1/teams/${sportSlug}/${teamSlug}`, { params }).then((r) => r.data);

export const search = (q, limit = 8) =>
  api.get("/v1/search", { params: { q, limit } }).then((r) => r.data);

// ── Athletes ───────────────────────────────────────────────────────────────

/**
 * Browse athletes.
 *
 * NOTE the doubled "/v1/v1": the route is declared with a /v1 prefix on a
 * router already mounted at /v1. The deployed API uses that shape — it is not
 * a typo, and collapsing it 404s.
 */
export const filterPlayers = (filters, { limit = 21, skip = 0 } = {}) =>
  api
    .post("/v1/v1/common_filter_players", filters, { params: { limit, skip } })
    .then((r) => r.data);

export const getPlayer = (playerId) =>
  api.get(`/v1/players/${playerId}`).then((r) => r.data);

export const getPlayerBySlug = (slug) =>
  api.get(`/v1/get_player_by_slug/${slug}`).then((r) => r.data);

export const getTeamRoster = (teamId, { limit = 21, skip = 0 } = {}) =>
  api
    .get(`/v1/get_players_by_team/${teamId}`, { params: { limit, skip } })
    .then((r) => r.data);

export default api;
